import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pagination, { PaginationPresentationProps } from "@waytek/MobiusOverrides/Pagination/Pagination";
import { fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDesktop = "showDesktop",
    showMobile = "showMobile",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDesktop]: boolean;
        [fields.showMobile]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCategoryContext;

export interface ContentBlockStyles {
    wrapper?: InjectableCss;
    paginationStyles?: PaginationPresentationProps;
    paginationWrapper?: InjectableCss;
    content?: TypographyProps;
}

export const ContentBlockStyles: ContentBlockStyles = {
    wrapper: {
        css: css`
            flex-wrap: wrap;
            width: 100%;
            max-width: 100%;
            margin-bottom: 20px;
            margin-top: 45px;
            display: none;

            &.show-mobile {
                ${mediaQueriesBP.maxMD} {
                    display: flex;
                    margin-top: 0;
                }
            }

            &.show-desktop {
                ${mediaQueriesBP.minMD} {
                    display: flex;
                }
            }

            &.show-mobile.show-desktop {
                display: flex;
            }
        `,
    },
    paginationWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            width: 100%;
            ${mediaQueriesBP.maxMD} {
                justify-content: center;
            }
        `,
    },
    // override the basic styles from the external content manager to match the style guide
    content: {
        css: css`
            p,
            span {
                font-size: 16px !important;
                line-height: 1.5;
                font-family: ${fontFamilyAlt} !important;
            }
        `,
    },
};

const styles = ContentBlockStyles;

const ProductListContentBlockSuperior = ({ addProductFilters, pagination, fields, category }: Props) => {
    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (!pagination || !pagination.totalItemCount || !category || !category.htmlContent) {
        return null;
    }

    const desktopClass = fields.showDesktop ? "show-desktop" : "";
    const mobileClass = fields.showMobile ? "show-mobile" : "";

    return (
        <StyledWrapper {...styles.wrapper} className={`${desktopClass} ${mobileClass}`}>
            <StyledWrapper>
                <Typography {...styles.content}>{parse(category.htmlContent, parserOptions)}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.paginationWrapper}>
                <Pagination
                    {...styles.paginationStyles}
                    resultsCount={pagination.totalItemCount}
                    currentPage={pagination.page}
                    resultsPerPage={pagination.pageSize}
                    resultsPerPageOptions={pagination.pageSizeOptions}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    pageSizeCookie="ProductList-PageSize"
                    data-test-selector="productList_pagination"
                    showResultsPerPage={false}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(ProductListContentBlockSuperior)),
    definition: {
        allowedContexts: ["ProductListPage"],
        group: "Product List",
        displayName: "Superior Content Block",
        fieldDefinitions: [
            {
                name: fields.showDesktop,
                displayName: "Show on Desktop",
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
                isRequired: false,
            },
            {
                name: fields.showMobile,
                displayName: "Show on Mobile",
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
